import React, { use, useEffect, useState } from 'react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import axiosInstance from '../../configuration/request';
import { useSnackbar } from '../../component/SnackbarContext';
import { Player } from "@lottiefiles/react-lottie-player";
import notFound from "../../assets/animation/notFount.json";
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowRightLong, FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { Breadcrumbs, Link, Typography } from '@mui/material'; // Import MUI Breadcrumb components
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { setNotificationCount } from '../../Redux/Slices/counterSlice';

const NotificationPanel = ({ userType }) => {
  const navigate = useNavigate(); // Initialize navigate
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { loginDetails } = useSelector((state) => state.counter);
  const userId = loginDetails.userId || null;

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 5;

  useEffect(() => {
    fetchNotification();
  }, [page]);

  useEffect(() => {
    dispatch(setNotificationCount(unreadCount));
  }, [unreadCount]);

  const fetchNotification = () => {
    if (userType === 'admin') {
      fetchAdminNotification();
    } else {
      fetchUserNotification();
    }
  };

  const fetchAdminNotification = () => {
    axiosInstance
      .get(`/notification/getNotificationsForAdmin?page=${page}&limit=${limit}`)
      .then((res) => {
        const rawData = res?.data?.data || [];
        const mappedNotifications = rawData.map((item) => ({
          id: item.id,
          title: item.message,
          read: item.read,
          time: new Date(item.createdAt).toLocaleString(),
          text: item?.comment,
          webLink: item?.web_link
        }));

        setNotifications(mappedNotifications);
        setTotalPages(res?.data?.totalPages || 1);
        setUnreadCount(res?.data?.unreadCount || 0);
      })
      .catch((err) =>
        showSnackbar(err?.response?.data?.message || "Something went wrong!", "error")
      );
  };

  const fetchUserNotification = () => {
    axiosInstance
      .get(`/notification/getNotifications?page=${page}&limit=${limit}`)
      .then((res) => {
        const rawData = res?.data?.data || [];
        const mappedNotifications = rawData.map((item) => ({
          id: item.id,
          title: item.message,
          read: item.read,
          time: new Date(item.createdAt).toLocaleString(),
          text: item?.comment,
          webLink: item?.web_link
        }));

        setNotifications(mappedNotifications);
        setTotalPages(res?.data?.totalPages || 1);
        setUnreadCount(res?.data?.unreadCount || 0);
      })
      .catch((err) =>
        showSnackbar(err?.response?.data?.message || "Something went wrong!", "error")
      );
  };

  const handleMarkUnread = (notificationIds) => {
    axiosInstance
      .patch(`/notification/mark-read/${notificationIds}`)
      .then(() => {
        fetchNotification();
      })
      .catch((err) =>
        showSnackbar(err?.response?.data?.message || "Something went wrong!", "error")
      );
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    const next = notifications.map((n) => (n.read ? n : { ...n, read: true }));
    setNotifications(next);
    const ids = unreadIds.join(",");
    handleMarkUnread(ids);
  };

  const toggleRead = (notification) => {
    const { id, read, webLink } = notification;

    if (!read) {
      const updated = notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      );
      setNotifications(updated);
      handleMarkUnread(id);
    }

    if (webLink) {
      window.location.href = webLink;
    }
  };

  return (
    <div className="w-full px-0 lgs:px-8 lg:px-16 pt-4">
      {/* Breadcrumbs Section */}
      <div className="mb-1">
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(-1); // Go back to the last page
            }}
            className="text-[#1B7EB8] cursor-pointer"
          >
           <p className='text-[#1B7EB8] font-bold '>Home</p> 
          </Link>
          <Typography color="text.primary" sx={{ fontSize: 'inherit', fontWeight: 'bold' }}>
            Notifications
          </Typography>
        </Breadcrumbs>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg xs:text-xl md:text-2xl font-bold text-[#222]">
          Notifications
        </h2>
        <button
          onClick={markAllAsRead}
          className="text-sm text-[#1B7EB8] font-semibold hover:underline"
        >
          Mark all as read
        </button>
      </div>

      <div className={`space-y-4 overflow-y-auto pr-2
        ${totalPages > 1 ? 'h-[calc(100vh-270px)]' : 'h-[calc(100vh-200px)]'}`} >
        {notifications.length > 0 ? (
          notifications.map((note) => (
            <div
              key={note.id}
              onClick={() => toggleRead(note)}
              className="flex items-start gap-4 p-4 border rounded-lg shadow-sm cursor-pointer transition bg-white hover:border-[#1B7EB8]"
            >
              <div className="relative w-10 h-10 flex items-center justify-center rounded-full bg-[#F2F9FF] text-[#1B7EB8] shadow-md">
                <NotificationsNoneIcon fontSize="small" />
                {!note.read && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-[#1B7EB8] border border-white" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-base font-medium ${note.read ? "text-gray-500" : "text-[#222]"}`}>
                  {note.title}
                </p>
                <p className={`text-[15px] sm:text-sm ${note.read ? "text-gray-500" : "text-[#222]"}`}>
                  {note.text}
                </p>
                <span className="text-xs sm:text-sm text-gray-400 mt-1 block">
                  {note.time}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col bg-white items-center justify-center py-10 text-gray-500">
            <Player autoplay loop src={notFound} style={{ height: "200px", width: "200px" }} />
            <p className="text-center text-sm md:text-base">No notification found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            className="w-8 h-8 rounded-full bg-white font-extrabold text-[#02537F] border-2 border-[#02537F] hover:bg-gray-100 disabled:opacity-40 flex items-center justify-center"
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            <FaArrowLeftLong className="text-lg" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((n) => n === 1 || n === totalPages || (n >= page - 2 && n <= page + 2))
            .map((n, idx, arr) => {
              const prev = arr[idx - 1];
              return (
                <React.Fragment key={n}>
                  {prev && n - prev > 1 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => setPage(n)}
                    className={`w-8 h-8 rounded-full text-xs font-medium ${
                      page === n ? "bg-[#02537F] text-white" : "bg-white border-2 border-[#E4E7E9]  hover:bg-gray-100"
                    }`}
                  >
                    {n}
                  </button>
                </React.Fragment>
              );
            })}

          <button
            className="w-8 h-8 rounded-full bg-white font-extrabold text-[#02537F] border-2 border-[#02537F] hover:bg-gray-100 disabled:opacity-40 flex items-center justify-center"
            disabled={page === totalPages}
            onClick={() => setPage((prev) => prev + 1)}
          >
            <FaArrowRightLong className="text-lg" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
